import { Link } from 'react-router-dom'

export default function HowTo() {
  const steps = [
    {
      title: 'Sign in with Google or email',
      description:
        'Use either your Google account or email and password to sign into BoxHand. Every user can create their own savings boxes and also join boxes created by others.',
    },
    {
      title: 'Create or clone a box',
      description:
        'Set the contribution amount, cycle duration and start date, then hit “Create Box.” Or clone a box you organize to reuse members, schedule, and settings with a fresh start date.',
    },
    {
      title: 'Invite members',
      description:
        'Send invites by email. Each invitation contains a secure link generated by the app; once accepted, the member is added automatically.',
    },
    {
      title: 'Track contributions & payouts',
      description:
        'Use the Schedule Builder to lock a fair rotation, then record payouts and contributions in the ledger. Organizers add entries; members can see history and upcoming payouts.',
    },
    {
      title: 'See your overall activity',
      description:
        'The “My Activity” page lists every contribution or payout you have recorded—whether you were the organizer or just a member.',
    },
  ]

  const quickChecks = [
    'Confirm your start date matches the group expectation before sending invites.',
    'Set cycle months and payouts per month so total slots divide evenly by members, then save the schedule.',
    'After everyone accepts, hit Auto distribute, adjust slots (you can stack multiple members per slot), and save to lock the rotation.',
    'Guardrail: saves will fail if any month’s payouts exceed total monthly contributions. Adjust slots or cycle length to fix it.',
    'Promote a member to organizer only when you need to hand over control.',
  ]

  const scenarios = [
    {
      title: 'One payout per month (simple rotation)',
      flow: [
        'Create box with cycle months equal to member count and payouts per month = 1.',
        'Invite everyone; once all accept, visit the Schedule tab and click “Save schedule” to lock the rotation.',
        'Each month, pay the listed member and record a `payout` entry in the ledger.',
      ],
    },
    {
      title: 'Stacked payouts in a month',
      flow: [
        'Set payouts per month to your needed slots (e.g., 1) and allow multiple members in the same slot.',
        'Use desired payouts per cycle to give members multiple payouts; auto-distribute, then stack members where needed.',
        'Guardrail ensures total monthly payouts do not exceed combined monthly contributions.',
      ],
    },
    {
      title: 'Long cycle with repeats',
      flow: [
        'Pick a longer cycle (e.g., 12 months) and payouts per month = 1 even if you have fewer members.',
        'Auto-distribute to repeat members across the full cycle; each member appears the same number of times.',
        'Save the schedule to prevent uneven setups; the table will flag errors if distribution is off.',
      ],
    },
    {
      title: 'Clone a past box',
      flow: [
        'From “Create box,” click “Clone existing box” (available to organizers).',
        'Pick a box you own; adjust name, start date, and contribution if needed.',
        'Create to copy members, desired payouts, and schedule with a new start date.',
      ],
    },
  ]

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto py-8 space-y-10">
        <div className="text-center">
          <p className="text-sm uppercase tracking-wide text-primary-600 font-semibold">How it Works</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2">Save together in five easy steps</h1>
          <p className="mt-4 text-base text-gray-600 max-w-3xl mx-auto">
            BoxHand helps friends, families and communities run rotating savings groups. Follow the steps,
            then bookmark the quick checks below to keep every cycle on track.
          </p>
        </div>

        <div className="space-y-6">
          {steps.map((step, index) => (
            <div key={step.title} className="bg-white shadow rounded-2xl border border-gray-100 p-6 sm:p-8 relative">
              <span className="absolute -top-3 -left-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary-600 text-white font-semibold">
                {index + 1}
              </span>
              <h2 className="text-xl font-semibold text-gray-900">{step.title}</h2>
              <p className="mt-3 text-gray-600 leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900">Before you start a cycle</h3>
            <p className="mt-2 text-sm text-gray-600">
              Run through these checks to reduce confusion for members and prevent ledger clean-up later.
            </p>
            <ul className="mt-4 space-y-3">
              {quickChecks.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-1 h-2.5 w-2.5 rounded-full bg-primary-500 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <Link
            to="/versions"
            className="rounded-3xl bg-gradient-to-br from-primary-600 to-primary-500 text-white p-6 flex flex-col justify-between shadow-lg transition-transform hover:-translate-y-0.5"
          >
            <div>
              <p className="text-sm uppercase tracking-wide text-primary-100 font-semibold">New</p>
              <h3 className="text-xl font-bold mt-2">See what changed</h3>
              <p className="mt-2 text-primary-50">
                Review the latest version notes and feature highlights before you make updates to your box.
              </p>
            </div>
            <div className="mt-4">
              <span className="inline-flex items-center rounded-full bg-white/20 px-3 py-2 text-sm font-semibold">
                View versions →
              </span>
            </div>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {scenarios.map((scenario) => (
            <div key={scenario.title} className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900">{scenario.title}</h3>
              <ul className="mt-4 space-y-3">
                {scenario.flow.map((step) => (
                  <li key={step} className="flex items-start gap-3 text-sm text-gray-700">
                    <span className="mt-1 h-2 w-2 rounded-full bg-primary-500 flex-shrink-0" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-gray-900 text-white rounded-3xl p-8 shadow-lg">
          <h3 className="text-2xl font-semibold">Need extra help?</h3>
          <p className="mt-3 text-gray-200">
            Reach out to your organizer or the BoxHand support team if you have questions about invites, contributions,
            or payouts. Include screenshots and the box ID so we can help faster.
          </p>
        </div>
      </div>
    </div>
  )
}
